import {
    AdditionalInsuredEndorsementEncoded,
} from '@/modules/document-data-review/models/AdditionalInsuredEndorsement.model';
import { FieldValidator } from '@/modules/dynamic-form/models/FieldValidator.model';
import { ValidationRules } from '@/modules/dynamic-form/models/ValidationRuleNames.model';

import { interpretMessage } from '../../utils/interpret-message/interpretMessage';

const REQUIRED_MESSAGE = 'is required';

// SubFieldKey matches the encoded structure keys, including nested paths
type SubFieldKey = keyof AdditionalInsuredEndorsementEncoded | 'details.additionalInsuredName';

export interface ItemFieldValidator extends FieldValidator {
    // Validates a sub-field value with access to the full item context
    validateWithContext: (fieldValue: any, itemValue: AdditionalInsuredEndorsementEncoded) => string | boolean;
}

function createConditionalRequiredValidator(
    fieldName: string,
    condition: (itemValue: AdditionalInsuredEndorsementEncoded) => boolean,
    customMsg?: string,
): ItemFieldValidator {
    const message = customMsg || REQUIRED_MESSAGE;

    function validateWithContext(fieldValue: any, itemValue: AdditionalInsuredEndorsementEncoded): string | boolean {
        // If condition is not met, field is not required
        if (!condition(itemValue)) {
            return true;
        }

        // Check if value is empty
        if (fieldValue === undefined || fieldValue === '' || fieldValue === null) {
            return interpretMessage(message, { field: fieldName });
        }

        return true;
    }

    // Standard validate function for compatibility (validates without context)
    function validate(fieldValue: any): string | boolean {
        if (fieldValue === undefined || fieldValue === '' || fieldValue === null) {
            return interpretMessage(message, { field: fieldName });
        }
        return true;
    }

    return {
        name: 'required',
        validate,
        validateWithContext,
    };
}

function createRequiredValidator(
    fieldName: string,
    customMsg?: string,
): ItemFieldValidator {
    return createConditionalRequiredValidator(
        fieldName,
        () => true, // Always required
        customMsg,
    );
}

/**
 * Creates a validator for the nested details object that validates additionalInsuredType.
 */
function createDetailsValidator(customMsg?: string): ItemFieldValidator {
    const message = customMsg || REQUIRED_MESSAGE;

    function validateWithContext(_fieldValue: any, itemValue: AdditionalInsuredEndorsementEncoded): string | boolean {
        // Only validate details when endorsementType is ADDITIONAL_INSURED
        if (itemValue.endorsementType !== 'ADDITIONAL_INSURED') {
            return true;
        }

        // Check if details.additionalInsuredType is provided
        if (!itemValue.details?.additionalInsuredType) {
            return interpretMessage(message, { field: 'Additional Insured Type' });
        }

        return true;
    }

    function validate(fieldValue: any): string | boolean {
        // Without context, we can only check if details exists
        if (fieldValue == null) {
            return interpretMessage(message, { field: 'Details' });
        }
        return true;
    }

    return {
        name: 'required',
        validate,
        validateWithContext,
    };
}

/**
 * Creates a validator for details.additionalInsuredName.
 * Required when details.additionalInsuredType === 'NAMED'.
 * Note: null is allowed (means explicitly not found), but undefined or empty string requires validation.
 */
function createAdditionalInsuredNameValidator(customMsg?: string): ItemFieldValidator {
    const message = customMsg || REQUIRED_MESSAGE;

    function validateWithContext(_fieldValue: any, itemValue: AdditionalInsuredEndorsementEncoded): string | boolean {
        // Only required when additionalInsuredType is NAMED
        if (itemValue.details?.additionalInsuredType !== 'NAMED') {
            return true;
        }

        const name = itemValue.details?.additionalInsuredName;
        // null is allowed (explicitly not found), but undefined or empty string is not
        if (name === undefined || name === '') {
            return interpretMessage(message, { field: 'Additional Insured' });
        }

        return true;
    }

    function validate(fieldValue: any): string | boolean {
        // Without context, check for undefined or empty string (null is allowed)
        if (fieldValue === undefined || fieldValue === '') {
            return interpretMessage(message, { field: 'Additional Insured' });
        }
        return true;
    }

    return {
        name: 'required',
        validate,
        validateWithContext,
    };
}

export function createAdditionalInsuredEndorsementValidatorsMap(
    _nullable: boolean,
    customMessages?: Record<ValidationRules, string>,
): Record<SubFieldKey | '$self', ItemFieldValidator[]> {
    const customMsg = customMessages?.required;

    return {
        // Self validator for the entire item (e.g., checking if the whole item is provided)
        '$self': [
            createRequiredValidator('Endorsement', customMsg),
        ],
        // endorsementType - always required
        'endorsementType': [
            createRequiredValidator('Endorsement Type', customMsg),
        ],
        // additionalInsuredEndorsementType - required when endorsementType === 'ADDITIONAL_INSURED'
        'additionalInsuredEndorsementType': [
            createConditionalRequiredValidator(
                'Additional Insured Endorsement Type',
                (item) => item.endorsementType === 'ADDITIONAL_INSURED',
                customMsg,
            ),
        ],
        // formNumber - required when additionalInsuredEndorsementType === 'FORM_NUMBER'
        'formNumber': [
            createConditionalRequiredValidator(
                'Form Number',
                (item) => {
                    const isFormNumber = item.additionalInsuredEndorsementType === 'FORM_NUMBER';
                    const isUnknown = item.endorsementType === 'UNKNOWN';
                    return isFormNumber || isUnknown;
                },
                customMsg,
            ),
        ],
        // details - validates nested additionalInsuredType (required when ADDITIONAL_INSURED)
        'details': [
            createDetailsValidator(customMsg),
        ],
        // details.additionalInsuredName - required when details.additionalInsuredType === 'NAMED'
        'details.additionalInsuredName': [
            createAdditionalInsuredNameValidator(customMsg),
        ],
    };
}

